import { Children, Fragment, type ElementType, type ReactNode } from 'react';
import { Box, type BoxProps } from '@mui/material';

type ResponsiveValue<T> = T | { xs?: T; sm?: T; md?: T; lg?: T; xl?: T };

interface StackProps extends Omit<BoxProps, 'component'> {
  component?: ElementType | undefined;
  direction?: ResponsiveValue<'row' | 'row-reverse' | 'column' | 'column-reverse'> | undefined;
  gap?: ResponsiveValue<number | string> | undefined;
  spacing?: ResponsiveValue<number | string> | undefined;
  alignItems?: ResponsiveValue<string> | undefined;
  justifyContent?: ResponsiveValue<string> | undefined;
  flexWrap?: ResponsiveValue<'nowrap' | 'wrap' | 'wrap-reverse'> | undefined;
  divider?: ReactNode;
  children?: ReactNode;
}

export function Stack({
  component,
  direction = 'column',
  gap,
  spacing,
  alignItems,
  justifyContent,
  flexWrap,
  divider,
  children,
  sx,
  ...props
}: StackProps) {
  const items = Children.toArray(children);
  const content = divider
    ? items.flatMap((child, index) =>
        index === 0 ? [child] : [<Fragment key={`divider-${index}`}>{divider}</Fragment>, child],
      )
    : children;
  return (
    <Box
      component={component ?? 'div'}
      {...props}
      sx={[
        {
          display: 'flex',
          flexDirection: direction,
          gap: gap ?? spacing,
          alignItems,
          justifyContent,
          flexWrap,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {content}
    </Box>
  );
}
